import { useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/auth/AuthContext'
import { useSiteSettings } from '@/siteSettingsContext'

const LOGIN_NAVY = '#0a192f'
const LOGIN_TEAL = '#2a7a76'

export function Login() {
  const { user, loading, login, mfaEnabled, requireMfa } = useAuth()
  const { siteTitle, logoText, appVersion } = useSiteSettings()
  const navigate = useNavigate()

  const location = useLocation()
  const from = location.state?.from || '/'

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const [mfaStep, setMfaStep] = useState(false)
  const [rememberMfa, setRememberMfa] = useState(true)
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const initials = (logoText || 'BK').slice(0, 4).toUpperCase()
  const versionLabel = appVersion
    ? `v${String(appVersion).replace(/^v/i, '')}`
    : null

  if (!loading && user) {
    if (user.must_change_password) {
      return <Navigate to="/change-password" replace />
    }
    if (requireMfa && !mfaEnabled) {
      return <Navigate to="/mfa/setup" replace />
    }
    return <Navigate to={from} replace />
  }

  if (loading) {
    return (
      <div
        className="flex min-h-screen items-center justify-center p-4"
        style={{ backgroundColor: LOGIN_NAVY }}
      >
        <p className="text-white/70">Loading…</p>
      </div>
    )
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    setSubmitting(true)
    try {
      const loggedIn = await login(
        email.trim(),
        password,
        mfaStep ? totpCode.trim() : undefined,
        rememberMfa,
      )
      if (loggedIn.must_change_password) {
        navigate('/change-password', { replace: true })
      } else if (requireMfa && !loggedIn.totp_enabled) {
        navigate('/mfa/setup', { replace: true })
      } else {
        navigate(from, { replace: true })
      }
    } catch (err) {
      const detail = err.response?.data?.detail
      if (detail && typeof detail === 'object' && detail.code === 'MFA_REQUIRED') {
        setMfaStep(true)
        setError('')
      } else {
        setError(
          typeof detail === 'string'
            ? detail
            : 'Sign in failed. Check your details and try again.',
        )
      }
    } finally {
      setSubmitting(false)
    }
  }

  const inputClass =
    'mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-[15px] text-slate-900 outline-none transition focus:border-[#2a7a76] focus:ring-2 focus:ring-[#2a7a76]/20'

  return (
    <div className="flex min-h-screen flex-col lg:flex-row">
      {/* Left: brand / value */}
      <aside className="relative flex flex-1 flex-col overflow-hidden px-8 py-10 text-white sm:px-12 lg:max-w-[50%] lg:px-14 lg:py-12">
        <div
          className="pointer-events-none absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/login-library.jpg')" }}
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background: `linear-gradient(160deg, ${LOGIN_NAVY}e0 0%, ${LOGIN_NAVY}b0 45%, ${LOGIN_NAVY}d0 100%)`,
          }}
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -bottom-24 -right-24 h-80 w-80 rounded-full opacity-35"
          style={{
            background: `radial-gradient(circle, ${LOGIN_TEAL}66 0%, transparent 70%)`,
          }}
          aria-hidden
        />

        <div className="relative flex items-center gap-2.5">
          <span
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-sm font-bold text-white"
            style={{ backgroundColor: LOGIN_TEAL }}
            aria-hidden
          >
            {initials}
          </span>
          <div className="min-w-0">
            <p className="truncate text-lg font-semibold tracking-tight text-white">
              {siteTitle}
            </p>
            {versionLabel ? (
              <p className="text-[11px] text-white/45">{versionLabel}</p>
            ) : null}
          </div>
        </div>

        <div className="relative mt-12 max-w-lg flex-1 lg:mt-20">
          <p
            className="text-[11px] font-semibold uppercase tracking-[0.18em]"
            style={{ color: '#7eb8b4' }}
          >
            Customer knowledge, clearly organised
          </p>
          <h1 className="mt-4 font-display text-3xl font-semibold leading-tight tracking-tight text-white sm:text-4xl lg:text-[2.65rem] lg:leading-[1.15]">
            Bakat IT Customer Portal
          </h1>
          <p className="mt-5 text-base leading-relaxed text-white/75 sm:text-[17px]">
            A secure workspace for customers systems, policies and day-to-day IT
            details
          </p>
        </div>

        <ol className="relative mt-10 space-y-3 text-sm text-white/85 sm:text-[15px] lg:mt-auto lg:pt-16">
          <li className="flex gap-3">
            <span
              className="w-7 shrink-0 font-semibold tabular-nums"
              style={{ color: '#7eb8b4' }}
            >
              01
            </span>
            <span>Documentation systems, contacts and procedures</span>
          </li>
          <li className="flex gap-3">
            <span
              className="w-7 shrink-0 font-semibold tabular-nums"
              style={{ color: '#7eb8b4' }}
            >
              02
            </span>
            <span>Granular Customer access to their own information</span>
          </li>
        </ol>
      </aside>

      {/* Right: sign-in */}
      <main className="flex flex-1 flex-col justify-center bg-white px-8 py-12 sm:px-12 lg:px-16 xl:px-20">
        <div className="mx-auto w-full max-w-md">
          <p
            className="flex items-center gap-2 text-sm font-medium"
            style={{ color: LOGIN_TEAL }}
          >
            <span
              className="inline-block h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: LOGIN_TEAL }}
              aria-hidden
            />
            Secure customer portal
          </p>

          <p
            className="mt-8 text-[11px] font-semibold uppercase tracking-[0.16em]"
            style={{ color: LOGIN_TEAL }}
          >
            Welcome back
          </p>
          <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-[1.75rem]">
            {mfaStep ? 'Confirm with your authenticator' : 'Sign in to continue'}
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-500">
            {mfaStep
              ? 'Enter the 6-digit code from your authenticator app to finish signing in.'
              : "Access your organisation's knowledge base with your managed account."}
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            {!mfaStep && (
              <>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-semibold text-slate-700"
                  >
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={submitting}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-semibold text-slate-700"
                  >
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    disabled={submitting}
                    className={inputClass}
                  />
                </div>
              </>
            )}

            {mfaStep && (
              <div>
                <label
                  htmlFor="totp"
                  className="block text-sm font-semibold text-slate-700"
                >
                  Authenticator code
                </label>
                <input
                  id="totp"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value)}
                  required
                  disabled={submitting}
                  className={`${inputClass} tracking-widest`}
                />
                <button
                  type="button"
                  className="mt-2 text-sm text-slate-500 underline decoration-slate-300 underline-offset-2 hover:text-slate-700"
                  onClick={() => {
                    setMfaStep(false)
                    setTotpCode('')
                    setError('')
                  }}
                >
                  Back to email and password
                </button>
                <label className="mt-3 flex items-start gap-2 text-sm text-slate-600">
                  <input
                    type="checkbox"
                    checked={rememberMfa}
                    onChange={(e) => setRememberMfa(e.target.checked)}
                    disabled={submitting}
                    className="mt-0.5"
                  />
                  <span>
                    Remember this browser for 7 days (skip authenticator code on
                    next sign-ins)
                  </span>
                </label>
              </div>
            )}

            {error && (
              <p className="text-sm text-red-700" role="alert">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="flex w-full items-center justify-center gap-2 rounded-lg px-4 py-3 text-[15px] font-semibold text-white transition hover:brightness-110 disabled:opacity-60"
              style={{ backgroundColor: LOGIN_TEAL }}
            >
              {submitting
                ? 'Signing in…'
                : mfaStep
                  ? 'Verify and sign in'
                  : 'Sign in to your workspace'}
              {!submitting ? <span aria-hidden>→</span> : null}
            </button>
          </form>

          <p className="mt-10 text-xs leading-relaxed text-slate-400">
            Access is limited to authorised staff and customer accounts. Do not
            share your password or authenticator codes.
          </p>
        </div>
      </main>
    </div>
  )
}
